import React, { useRef, useState, useEffect, useContext, useReducer } from 'react';
import { toast } from 'react-toastify';
import { green } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import SyncOutlinedIcon from '@material-ui/icons/SyncOutlined';

import { AuthContext } from '../../context/Auth/AuthContext';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import BuyResource from '../../components/BuyResource';
import ConfirmationModal from '../../components/ConfirmationModal';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import TemplateModal from '../../components/TemplateModal';
import Title from '../../components/Title';
import toastError from '../../errors/toastError';

import SocketTemplate from '../../context/Socket/Listeners/SocketTemplate';

const reducer = (state, action) => {
  const handleUpdateTemplate = (template) => {
    const templateIndex = state.findIndex(t => t.templateUuid === template.templateUuid);

    if (templateIndex !== -1) {
      state[templateIndex] = template;
      return [...state];
    }

    return [template, ...state];
  };

  if (action.type === 'LOAD_TEMPLATES') {
    const templates = action.payload;
    const newTemplates = [];

    templates.forEach(template => {
      const templateIndex = state.findIndex(t => t.templateUuid === template.templateUuid);
      if (templateIndex !== -1) state[templateIndex] = template;
      else newTemplates.push(template);
    });

    return [...state, ...newTemplates];
  }

  if (action.type === 'UPDATE_TEMPLATES') {
    return handleUpdateTemplate(action.payload);
  }

  if (action.type === 'BULK_UPDATE_TEMPLATES') {
    let templates = [];
    let templateIndex = 0;

    for (const template of action.payload) {
      if (templateIndex === action.payload.length - 1) templates = handleUpdateTemplate(template);
      else handleUpdateTemplate(template);
      templateIndex += 1;
    }

    return templates;
  }

  if (action.type === 'DELETE_TEMPLATE') {
    const templateUuid = action.payload;
    const templateIndex = state.findIndex(t => t.templateUuid === templateUuid);
    if (templateIndex !== -1) state.splice(templateIndex, 1);
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
  },

  floatingButton: {
    transition: 'transform 0.30s',
    '&:hover': { transform: 'translateY(-5px)', },
  },

  buttonProgress: {
		color: green[500],
		position: 'absolute',
		top: '10%',
		left: '50%',
		marginTop: -12,
		marginLeft: -12,
	},

  templatesList: {
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
    height: '90%',
    [theme.breakpoints.down('sm')]: { maxHeight: '80%', },
  },

  icon: { color: theme.palette.text.primary, },

  actionButton: { '&:hover': { color: theme.palette.primary.main, }, },
}));

const Templates = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  
  const { user } = useContext(AuthContext);
  const isUserFullAccess = user.profile === 'admin' && user.configEnabled;

  const [templates, templateDispatch] = useReducer(reducer, []);

  const lastScroll = useRef({ left: 0, top: 0 });
  const templateToDeleteRef = useRef(undefined);
  const templateToEditRef = useRef(undefined);

  const [isLoadingMetaApiFetch, setIsLoadingMetaApiFetch] = useState(true);
  const [isMetaApiEnabled, setIsMetaApiEnabled] = useState(false);

  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [isConfirmDeletionModalOpen, setIsConfirmDeletionModalOpen] = useState(false);
  const [isUpsertModalOpen, setIsUpsertModalOpen] = useState(false);

  //  *****************
  //  ** Use-Effects ** 
  //  *****************
  useEffect(() => {
    const delayDebounceFN = setTimeout(() => {
      const fetchIsMetaApiEnabled = async () => {
        try {
          setIsLoadingMetaApiFetch(true);
          const { data } = await api.get(`accounts/${user.tenantId}`);
          setIsMetaApiEnabled(data.isMetaApiEnabled);
        }
        catch (exception) {
          console.log('Templates useEffect fetchIsMetaApiEnabled Exception:', exception);
          toastError(exception);
        }
        finally {
          setIsLoadingMetaApiFetch(false);
        }
      };

      void fetchIsMetaApiEnabled();
    }, 500);

    return () => clearTimeout(delayDebounceFN);
  }, [user]);

  useEffect(() => {
    const delayDebounceFN = setTimeout(() => {
      const fetchTemplates = async () => {
        try {
          setIsLoadingTemplates(true);

          const { data } = await api.get('/officialApi/template', { params: { pageNumber } });
          templateDispatch({ type: 'LOAD_TEMPLATES', payload: data.templates });
          setHasMore(data.hasMore);
        }
        catch (exception) {
          console.log('Templates useEffect fetchTemplates Exception:', exception);
          toastError(exception);
        }
        finally {
          setIsLoadingTemplates(false);
        }
      };

      void fetchTemplates();
    }, 500);

    return () => clearTimeout(delayDebounceFN);
  }, [pageNumber]);

  //  *************
  //  ** Methods ** 
  //  *************
  const handleOpenDeletionModal = (template) => {
    if (templateToDeleteRef.current) return;
    setIsConfirmDeletionModalOpen(true);
    templateToDeleteRef.current = template;
  };

  const handleCloseDeletionModal = () => {
    setIsConfirmDeletionModalOpen(false);
    templateToDeleteRef.current = undefined;
  };

  const handleOpenUpsertModal = (template) => {
    if (template && !['APPROVED', 'REJECTED', 'PAUSED'].includes(template.status)) {
      toast.warning(i18n.t('templates.toasts.invalidStatus'));
      return;
    }

    templateToEditRef.current = template;
    setIsUpsertModalOpen(true);
  };

  const handleCloseUpsertModal = () => {
    setIsUpsertModalOpen(false);
    setTimeout(() => templateToEditRef.current = undefined, 100);
  };

  const handleScroll = (event) => {
    const { scrollLeft, scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    const changedScrollHorizontal = scrollLeft !== lastScroll.current.left;
    const changedScrollVertical = scrollTop !== lastScroll.current.top;
    lastScroll.current = { left: scrollLeft, top: scrollTop };

    if (changedScrollHorizontal || !changedScrollVertical) return;
    if (!hasMore || isLoadingTemplates) return;
    
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(previousPageNumber => previousPageNumber + 1);
  };

  const handleDeleteTemplate = async () => {
    if (!isUserFullAccess) return;
    if (!templateToDeleteRef.current) return;
    if (isLoadingTemplates) return;

    try {
      setIsLoadingTemplates(true);
      await api.delete(`/officialApi/template/${encodeURIComponent(templateToDeleteRef.current.templateUuid)}`);
    }
    catch (exception) {
      console.log('handleDeleteTemplate exception:', exception);
      toastError(exception);
    }
    finally {
      templateToDeleteRef.current = undefined;
      setIsLoadingTemplates(false);
      setIsConfirmDeletionModalOpen(false);
    }
  };

  const handleSyncAllTemplates = async () => {
    if (!isUserFullAccess) return;
    if (isLoadingTemplates) return;
    
    try {
      setIsLoadingTemplates(true);
      await api.get('/officialApi/template/sync');
    }
    catch (exception) {
      console.log('handleSyncAllTemplates exception:', exception);
      toastError(exception);
    }
    finally {
      try {
        templateDispatch({ type: 'RESET' });

        const { data } = await api.get('/officialApi/template', { params: { pageNumber } });
        templateDispatch({ type: 'LOAD_TEMPLATES', payload: data.templates });
        setHasMore(data.hasMore);
      }
      catch (exception) {
        console.log('handleSyncAllTemplates finally exception:', exception);
        toastError(exception);
      }
      finally {
        setIsLoadingTemplates(false);
      }
    }
  };

  const handleSyncSingleTemplate = async (templateUuid) => {
    if (isLoadingTemplates) return;

    try {
      setIsLoadingTemplates(true);
      await api.get(`/officialApi/template/sync/${encodeURIComponent(templateUuid)}`);
    }
    catch (exception) {
      console.log('handleSyncSingleTemplate exception:', exception);
      toastError(exception);
    }
    finally {
      setIsLoadingTemplates(false);
    }
  };

  //  *************
  //  ** Renders ** 
  //  *************
  const renderLoadingMetaApiFetch = () => {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t('templates.title')}</Title>
        </MainHeader>

        <Paper className={classes.mainPaper} variant='outlined'>
          <CircularProgress size={24} className={classes.buttonProgress} />
        </Paper>
      </MainContainer>
    );
  };

  const renderMetaApiDisabled = () => {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t('templates.title')}</Title>
        </MainHeader>

        <Paper className={classes.mainPaper} variant='outlined'>
          <BuyResource />
        </Paper>
      </MainContainer>
    );
  };

  const renderMetaApiEnabled = () => {
    return (
      <MainContainer>
        <SocketTemplate dispatch={templateDispatch} />

        {isUserFullAccess && templateToDeleteRef.current && (
          <ConfirmationModal
            title={i18n.t('templates.confirmationModal.deleteTitle').replace('<param>', templateToDeleteRef.current.name)}
            open={isConfirmDeletionModalOpen}
            onClose={handleCloseDeletionModal}
            onConfirm={() => void handleDeleteTemplate()}
            shouldCloseOnConfirm={false}
          />
        )}

        {isUserFullAccess && (
          <TemplateModal open={isUpsertModalOpen} onClose={handleCloseUpsertModal} template={templateToEditRef.current} />
        )}

        <MainHeader>
          <Title>{i18n.t('templates.title')}</Title>

          {isUserFullAccess && (
            <MainHeaderButtonsWrapper>
              <Button
                variant='outlined'
                color='inherit'
                className={classes.floatingButton}
                onClick={() => void handleSyncAllTemplates()}
              >
                {i18n.t('templates.buttons.sync')}
              </Button>

              <Button
                variant='contained'
                color='primary'
                className={classes.floatingButton}
                onClick={() => handleOpenUpsertModal(undefined)}
              >
                {i18n.t('templates.buttons.add')}
              </Button>
            </MainHeaderButtonsWrapper>
          )}
        </MainHeader>

        <Paper className={classes.mainPaper} variant='outlined'>
          <div className={classes.templatesList} onScroll={handleScroll}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>{i18n.t('templates.table.name')}</TableCell>
                  <TableCell align='center'>{i18n.t('templates.table.status')}</TableCell>
                  <TableCell align='center'>{i18n.t('templates.table.qualityScore')}</TableCell>
                  <TableCell align='center'>{i18n.t('templates.table.language')}</TableCell>
                  <TableCell align='center'>{i18n.t('templates.table.parameterFormat')}</TableCell>
                  <TableCell align='center'>{i18n.t('templates.table.category')}</TableCell>
                  <TableCell align='center'>{i18n.t('templates.table.actions')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {templates.map(template => (
                  <TableRow key={template.templateUuid}>
                    <TableCell align='center'>{template.name}</TableCell>
                    <TableCell align='center'>{i18n.t(`templates.labels.status.${template.status}`)}</TableCell>
                    <TableCell align='center'>{i18n.t(`templates.labels.qualityScore.${template.quality_score}`)}</TableCell>
                    <TableCell align='center'>{template.language}</TableCell>
                    <TableCell align='center'>{i18n.t(`templates.labels.parameterFormat.${template.parameter_format}`)}</TableCell>
                    <TableCell align='center'>{i18n.t(`templates.labels.category.${template.category}`)}</TableCell>
                    <TableCell align='center'>
                      {isUserFullAccess && (
                        <>
                          <IconButton className={classes.icon} size='small' onClick={() => void handleSyncSingleTemplate(template.templateUuid)}>
                            <SyncOutlinedIcon className={classes.actionButton} />
                          </IconButton>

                          <IconButton className={classes.icon} size='small' onClick={() => handleOpenUpsertModal(template)}>
                            <EditIcon className={classes.actionButton} />
                          </IconButton>

                          <IconButton className={classes.icon} size='small' onClick={() => handleOpenDeletionModal(template)}>
                            <DeleteOutlineIcon className={classes.actionButton} />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {isLoadingTemplates && <TableRowSkeleton columns={7} />}
              </TableBody>
            </Table>
          </div>
        </Paper>
      </MainContainer>
    );
  };

  //  ************
  //  ** Return ** 
  //  ************
  if (isLoadingMetaApiFetch) return renderLoadingMetaApiFetch();
  if (!isMetaApiEnabled) return renderMetaApiDisabled();
  return renderMetaApiEnabled();
};

export default Templates;
